import { find, uniq, max, get, round, multiply } from 'lodash';
import { CardData, StorySettingsType } from 'types/story';
import { CARD_TYPE, DEFAULT_SCORE } from 'common/constants';
import { CardFacade } from 'common/utils/facades/card-facade';
import { clientLog, int } from 'common/utils/helpers';
import { lsNames } from 'client/constants/common';
import produce from 'immer';

const log = clientLog.extend('StoryHistory');

type StoryId = string;
type CardId = string;
type StoredDataT = Record<
	StoryId,
	{
		// current play(cp). temporary. initialized on every page load.
		cp?: { cards: Record<CardId, { answers?: string[] /* answer id arr */ }> };
		// last visited card(lvc). permanent. do not delete it.
		lvc?: CardId;
	}
>;

const defaultStoryData: StoredDataT[StoryId] = {
	cp: { cards: {} },
};

export class StoryHistory {
	storyId: string;

	itemName = lsNames.history;

	constructor({ storyId = '' }) {
		this.storyId = storyId;
	}

	get history(): StoredDataT {
		const item = localStorage.getItem(this.itemName);
		const itemParsed: StoredDataT = JSON.parse(item || JSON.stringify({}));

		if (!itemParsed[this.storyId]) {
			itemParsed[this.storyId] = defaultStoryData;
		}

		if (!itemParsed[this.storyId]?.cp) {
			itemParsed[this.storyId].cp = defaultStoryData.cp;
		}

		return itemParsed;
	}

	get lastVisitedCard(): string | undefined {
		const story = this.history[this.storyId];
		return story.lvc ?? (story as any).cardId /* right operand is an old syntax <= release v1.11.7 */;
	}

	isCardVisited({ cardId }: { cardId: string }) {
		return this.history[this.storyId].cp?.cards[cardId] !== undefined;
	}

	clearLastPlay() {
		localStorage.setItem(
			this.itemName,
			JSON.stringify(
				produce(this.history, draft => {
					new Set([...Object.keys(draft), this.storyId]).forEach(storyId => {
						if (storyId === this.storyId) draft[storyId].cp = defaultStoryData.cp;
						else delete draft[storyId].cp;
					});
				})
			)
		);
	}

	addCard({ cardId }: { cardId: string }) {
		localStorage.setItem(
			this.itemName,
			JSON.stringify(
				produce(this.history, draft => {
					draft[this.storyId].lvc = cardId;
					draft[this.storyId].cp!.cards[cardId] = {};
				})
			)
		);
	}

	addAnswer({ answerId, cardId, answersMax = 1 }: { answerId: string; cardId: string; answersMax?: number }) {
		const { history } = this;
		const prevAnswers = history[this.storyId].cp!.cards[cardId]?.answers ?? [];
		const answers = answersMax > 1 ? uniq([...prevAnswers, answerId]) : [answerId];

		localStorage.setItem(
			this.itemName,
			JSON.stringify(
				produce(history, draft => {
					draft[this.storyId].cp!.cards[cardId].answers = answers;
				})
			)
		);
	}

	/**
	 * Get score for played cards
	 */
	totalResults(storySettings: StorySettingsType, cards: CardData[]) {
		const RESULT = {
			scoreMax: 0, // Maximum score - the sum of the maximum score for the cards played
			scoreTotal: 0, // Total score - the amount of score received for the cards played
			scoreRelative: 0, // Relative score - the ratio of the total score to the maximum (0 - 100)
			answersTotal: 0,
			answersCorrect: 0,
		};

		const { cp: currentPlay } = this.history[this.storyId];
		Object.entries(currentPlay!.cards).forEach(([cardId, { answers: chosenAnswerIds = [] }]) => {
			const { score, answers = {} } = storySettings.cards?.[cardId] ?? {};
			const cardData = find(cards, ['_id', cardId]);
			const canScore = cardData ? new CardFacade(cardData).hasFeature.score : false;
			const defaultCardScore = canScore ? DEFAULT_SCORE : 0;

			// Get card score
			const cardScore = int(score ?? defaultCardScore);
			if (cardScore) RESULT.scoreMax += cardScore;

			// Get max answer score per card (e.g. PersonalityTest card)
			const defaultAnswerScore = cardData?.type === CARD_TYPE.PERSONALITY_TEST ? DEFAULT_SCORE : 0;
			const answerMaxScore = max(Object.values(answers).map(answer => int(answer.score ?? defaultAnswerScore)));
			if (answerMaxScore) RESULT.scoreMax += answerMaxScore;

			/* count answers. each visited card that can score points must increase the "answersTotal" value.
			   If the user did not answer, it will be considered that he answered incorrectly. */
			if (canScore) RESULT.answersTotal += 1;

			// sum up the score for chosen answers
			chosenAnswerIds.forEach(answerId => {
				// Add card score for a correct answer (eg. Trivia card)
				// TODO: if answersMax > 1 score should be incremented partially in depends on count of correct answers
				const isCorrect = get(answers, `${answerId}.isCorrect`);
				RESULT.scoreTotal += isCorrect ? cardScore : 0;

				// inc counters
				if (canScore) if (isCorrect) RESULT.answersCorrect += 1;

				// Add answer score (PersonalityTest card)
				const answerScore = int(answers?.[answerId]?.score ?? defaultAnswerScore);
				if (answerScore) RESULT.scoreTotal += answerScore;
			});
		});

		RESULT.scoreRelative = multiply(RESULT.scoreTotal / RESULT.scoreMax || 0, 100);

		log('result', RESULT);

		return RESULT;
	}
}

class _AnswerDelta {
	private delta: Record<string, number> = {};

	start(cardId: string) {
		this.delta[cardId] = Date.now();

		log('_AnswerDelta start', this.delta[cardId]);
	}

	end(cardId: string) {
		if (!this.delta[cardId]) return;

		this.delta[cardId] = round((Date.now() - this.delta[cardId]) / 1000, 3);

		log('_AnswerDelta end', this.delta[cardId]);
	}

	get(cardId: string) {
		return this.delta[cardId];
	}
}

export const AnswerDelta = new _AnswerDelta();
